import { z } from 'zod';
import { VALIDATION_REGEX } from '@constants/patterns';
import { INPUT_CONSTRAINTS } from '@constants/inputs.constants';

const EMAIL_SCHEMA = z
  .string()
  .min(1, 'emailRequired')
  .email('emailInvalid')
  .toLowerCase();

export const PASSWORD_SCHEMA = z
  .string()
  .min(7, 'lengthValid')
  .max(40, 'maxLength')
  .regex(/[A-Z]/, 'capitalLetter')
  .regex(/[a-z]/, 'lowercaseLetter')
  .regex(/[0-9]/, 'containNumbers')
  .regex(/[!@#$%&*]/, 'specialCharacters')
  .refine((value) => !/\s/.test(value), {
    message: 'noWhitespace',
  });

const CONFIRMATION_CODE_SCHEMA = z
  .string()
  .min(1, 'codeRequired')
  .length(INPUT_CONSTRAINTS.CODE_LENGTH, 'codeLength')
  .regex(VALIDATION_REGEX.CODE, 'codeInvalid');

const CONFIRM_PASSWORD_SCHEMA = z.string().min(1, 'confirmPasswordRequired');

const UUID_SCHEMA = z.string().min(1, 'resetTokenRequired');

const PASSWORD_MATCH_REFINEMENT = {
  message: 'passwordsDoNotMatch',
  path: ['confirmPassword' as const],
};

export const legacyForgotPasswordSchema = z.object({
  email: EMAIL_SCHEMA,
});

export const legacyResetPasswordSchema = z
  .object({
    password: PASSWORD_SCHEMA,
    confirmPassword: CONFIRM_PASSWORD_SCHEMA,
    uuid: UUID_SCHEMA,
  })
  .refine(
    (data) => data.password === data.confirmPassword,
    PASSWORD_MATCH_REFINEMENT
  );

export const legacyPasswordFormSchema = z
  .object({
    password: PASSWORD_SCHEMA,
    confirmPassword: CONFIRM_PASSWORD_SCHEMA,
    uuid: z.string().optional(),
  })
  .refine(
    (data) => data.password === data.confirmPassword,
    PASSWORD_MATCH_REFINEMENT
  );

export const cognitoForgotPasswordSchema = z.object({
  email: EMAIL_SCHEMA,
});

export const cognitoResetPasswordSchema = z
  .object({
    email: EMAIL_SCHEMA.optional(),
    code: CONFIRMATION_CODE_SCHEMA.optional(),
    password: PASSWORD_SCHEMA,
    confirmPassword: CONFIRM_PASSWORD_SCHEMA,
  })
  .refine(
    (data) => data.password === data.confirmPassword,
    PASSWORD_MATCH_REFINEMENT
  );

export const cognitoConfirmPasswordSchema = z
  .object({
    email: EMAIL_SCHEMA,
    code: CONFIRMATION_CODE_SCHEMA,
    password: PASSWORD_SCHEMA,
    confirmPassword: CONFIRM_PASSWORD_SCHEMA,
  })
  .refine(
    (data) => data.password === data.confirmPassword,
    PASSWORD_MATCH_REFINEMENT
  );

export const cognitoMigrationPasswordSchema = z
  .object({
    email: EMAIL_SCHEMA,
    password: PASSWORD_SCHEMA,
    confirmPassword: CONFIRM_PASSWORD_SCHEMA,
  })
  .refine(
    (data) => data.password === data.confirmPassword,
    PASSWORD_MATCH_REFINEMENT
  );

export const resetPasswordSchema = z
  .object({
    email: EMAIL_SCHEMA,
    code: CONFIRMATION_CODE_SCHEMA,
    password: PASSWORD_SCHEMA,
    confirmPassword: CONFIRM_PASSWORD_SCHEMA,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'passwordsMustMatch',
    path: ['confirmPassword'],
  });

export const migrationPasswordSchema = z
  .object({
    password: PASSWORD_SCHEMA,
    confirmPassword: CONFIRM_PASSWORD_SCHEMA,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'passwordsMustMatch',
    path: ['confirmPassword'],
  });

export const confirmPasswordOnlySchema = z
  .object({
    password: PASSWORD_SCHEMA,
    confirmPassword: CONFIRM_PASSWORD_SCHEMA,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'passwordsMustMatch',
    path: ['confirmPassword'],
  });

export const forgotPasswordSchema = z.object({
  email: EMAIL_SCHEMA,
});

export type LegacyForgotPasswordData = z.infer<
  typeof legacyForgotPasswordSchema
>;
export type LegacyResetPasswordData = z.infer<typeof legacyResetPasswordSchema>;
export type LegacyPasswordFormData = z.infer<typeof legacyPasswordFormSchema>;

export type CognitoForgotPasswordData = z.infer<
  typeof cognitoForgotPasswordSchema
>;
export type CognitoResetPasswordData = z.infer<
  typeof cognitoResetPasswordSchema
>;
export type CognitoConfirmPasswordData = z.infer<
  typeof cognitoConfirmPasswordSchema
>;
export type CognitoMigrationPasswordData = z.infer<
  typeof cognitoMigrationPasswordSchema
>;

export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;
export type MigrationPasswordFormData = z.infer<typeof migrationPasswordSchema>;
export type ConfirmPasswordOnlyFormData = z.infer<
  typeof confirmPasswordOnlySchema
>;
export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export const validateSchema = <T>(schema: z.ZodSchema<T>, data: unknown): T =>
  schema.parse(data);

const FIRST_NAME_SCHEMA = z.string().min(1, 'regFirstNameValidation');
const LAST_NAME_SCHEMA = z.string().min(1, 'regLastNameValidation');
const SECOND_LAST_NAME_SCHEMA = z
  .string()
  .min(1, 'regSecondLastNameValidation');
const COMPANY_NAME_SCHEMA = z.string().min(1, 'regCompanyNameValidation');
const PHONE_SCHEMA = z.string().min(1, 'regPhoneValidation');
const GENDER_SCHEMA = z.enum(['Male', 'Female'], {
  errorMap: () => ({ message: 'regGenderValidation' }),
});
const DATA_CONFIRM_SCHEMA = z
  .boolean()
  .refine((val) => val === true, { message: 'requiredField' });

export const registerFormSchema = z
  .object({
    firstName: FIRST_NAME_SCHEMA,
    lastName: LAST_NAME_SCHEMA,
    secondLastName: SECOND_LAST_NAME_SCHEMA,
    representativePosition: z.string().optional(),
    companyName: COMPANY_NAME_SCHEMA,
    email: EMAIL_SCHEMA,
    referral: z.string().optional(),
    referralId: z
      .union([z.number(), z.string(), z.undefined()])
      .transform((val) => {
        if (val === undefined || val === '') return undefined;
        return Number(val);
      })
      .optional(),
    phone: PHONE_SCHEMA,
    password: PASSWORD_SCHEMA,
    confirmPassword: CONFIRM_PASSWORD_SCHEMA,
    gender: GENDER_SCHEMA,
    dataConfirm: DATA_CONFIRM_SCHEMA,
    nationalityPersonId: z
      .union([z.number(), z.string()])
      .transform((val) => String(val)),
    countryBirthId: z
      .union([z.number(), z.string()])
      .transform((val) => String(val)),
    federalEntityId: z
      .union([z.number(), z.string()])
      .transform((val) => String(val))
      .optional(),
    curp: z.string().optional(),
    masterAccountName: z.string().optional(),
    providerCode: z.string().optional(),
    isSubaccount: z.boolean().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'passwordsDoNotMatch',
    path: ['confirmPassword'],
  });

export const validateSchemaAsync = async <T>(
  schema: z.ZodSchema<T>,
  data: unknown
): Promise<T> => schema.parseAsync(data);
